// v2 footbridge head — a timber-crib abutment: three courses of crossed logs
// (log-cabin corners, ends proud) packed with a stone-rubble fill, carrying a
// bearer cap and a four-plank landing deck, with a chamfered newel post at each
// corner. Authored symmetric across both axes so it reads as the bridge ramp from
// either span direction. The walkable plank tops sit at y = 0.42. Ported 1:1 from
// age-of-mcp's v2/footbridge_head.ts, using the library's shared `foundationSlab`
// and `scaffold` helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, type Part } from "../../lib";

const LOG    = "#7a5630";   // crib logs
const LOG_DK = "#5e4226";   // alternate course / bearer cap
const FILL   = "#8d8a82";   // stone-rubble fill
const PLANK  = "#9a6a38";   // landing planks
const POST   = "#6b4a26";   // corner newels

const WALK_TOP = 0.42;      // the renderer's HEAD_TOP for this type — exact

// One crossed-log crib course at height `y`; `alongX` alternates per course.
function cribCourse(y: number, alongX: boolean, color: string): Part[] {
  const out: Part[] = [];
  for (const o of [-0.26, 0.26]) {
    out.push({
      geometry: new THREE.CylinderGeometry(0.05, 0.05, 0.72, 5),
      color,
      position: alongX ? [0, y, o] : [o, y, 0],
      rotation: alongX ? [0, 0, Math.PI / 2] : [Math.PI / 2, 0, 0],
    });
  }
  return out;
}

// The rubble fill packed inside the crib.
function fill(h: number): Part {
  return { geometry: new THREE.BoxGeometry(0.54, h, 0.54), color: FILL,
    position: [0, 0.04 + h / 2, 0] };
}

// The landing: a bearer cap under four planks whose tops are the walk surface.
function landing(): Part[] {
  const out: Part[] = [
    { geometry: new THREE.BoxGeometry(0.64, 0.04, 0.64), color: LOG_DK,
      position: [0, 0.38, 0] },
  ];
  for (const z of [-0.255, -0.085, 0.085, 0.255]) {
    out.push({ geometry: new THREE.BoxGeometry(0.68, 0.02, 0.145), color: PLANK,
      position: [0, WALK_TOP - 0.01, z] });
  }
  return out;
}

// A chamfer-capped newel post at each corner — symmetric on all four, so the
// head reads as a railing terminal whichever way the span runs.
function newels(): Part[] {
  const out: Part[] = [];
  for (const x of [-0.3, 0.3]) {
    for (const z of [-0.3, 0.3]) {
      out.push(
        { geometry: new THREE.BoxGeometry(0.07, 0.18, 0.07), color: POST,
          position: [x, WALK_TOP + 0.09, z] },
        { geometry: new THREE.ConeGeometry(0.055, 0.06, 4), color: LOG_DK,
          position: [x, WALK_TOP + 0.21, z], rotation: [0, Math.PI / 4, 0] },
      );
    }
  }
  return out;
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.78, 0.08)];
  if (s === 1) {
    return [foundationSlab(0.78, 0.08),
      ...cribCourse(0.12, true, LOG),
      ...scaffold(0.7, 0.22)];
  }
  if (s === 2) {
    return [foundationSlab(0.78, 0.08),
      ...cribCourse(0.12, true, LOG),
      ...cribCourse(0.22, false, LOG_DK),
      ...cribCourse(0.32, true, LOG),
      fill(0.32),
      ...scaffold(0.7, 0.38)];
  }
  return [
    foundationSlab(0.78, 0.08),
    ...cribCourse(0.12, true, LOG),
    ...cribCourse(0.22, false, LOG_DK),
    ...cribCourse(0.32, true, LOG),
    fill(0.32),
    ...landing(),
    ...newels(),
  ];
}

export const footbridgeHeadV2 = defineModel({
  id: "footbridge-head-v2",
  tiles: { w: 1, h: 1 },
  batchId: "fable",
  name: "Footbridge head (v2)",
  tags: ["building","footbridge","head","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly footbridge head.",
  description: "A ramp onto a footbridge; a pair on opposite banks spans a gap with a plank walkway.",
  author: "age-of-mcp",
  footprint: 0.42,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
});
